import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '../../shared/services/auth.service';
import { ROLE } from '../../shared/models/auth.model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [AppMenuitem, RouterModule],
  template: ` <ul class="layout-menu w-full flex flex-col gap-1 p-2">
    @for (item of filteredMenu; track item; let i = $index) {
      @if (!item.separator) {
        <li app-menuitem [item]="item" [index]="i" [root]="true"></li>
      }
      @if (item.separator) {
        <li class="menu-separator border-t border-surface my-2"></li>
      }
    }
  </ul>`
})
export class AppMenu implements OnInit, OnDestroy {
  filteredMenu: any[] = [];

  auth = inject(AuthService);
  translateService = inject(TranslateService);

  private langChangeSub!: Subscription;

  ngOnInit() {
    this.buildMenu();
    this.langChangeSub = this.translateService.onLangChange.subscribe(() => this.buildMenu());
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }

  private buildMenu() {
    const t = (key: string) => this.translateService.instant(key);
    const role = this.auth.getProfile().role;

    const model = [
      {
        label: t('menu.home'),
        items: [{ label: t('menu.dashboard'), icon: 'pi pi-home', routerLink: ['/dashboard'] }]
      },
      {
        label: t('menu.customer'),
        items: [
          {
            label: t('menu.customer_information'),
            icon: 'pi pi-user',
            routerLink: ['customer/customer-information']
          }
        ]
      },
      {
        label: t('menu.schedule'),
        items: [{ label: t('menu.calendar'), icon: 'pi pi-calendar', routerLink: ['/calendar'] }]
      },
      {
        label: t('menu.queue'),
        items: [
          {
            label: t('menu.check_opd'),
            icon: 'pi pi-clock',
            routerLink: ['/queue/check-opd']
          }
        ]
      },
      {
        label: t('menu.expense'),
        items: [
          { label: t('menu.invoice'), icon: 'pi pi-money-bill', routerLink: ['/expense/invoice'] },
          { label: t('menu.receipt'), icon: 'pi pi-wallet', routerLink: ['/expense/receipt-list'] },
          { label: t('menu.drug_label'), icon: 'icon icon-drug', routerLink: ['/expense/drug-label-list'] }
        ]
      },
      {
        label: t('menu.products'),
        items: [
          {
            label: t('menu.drug_equipment_list'),
            icon: 'pi pi-shop',
            routerLink: ['/products/drug-equipment-list'],
            roles: [ROLE.DOCTOR, ROLE.ADMIN]
          },
          {
            label: t('menu.label'),
            icon: 'pi pi-tag',
            routerLink: ['/products/label'],
            roles: [ROLE.DOCTOR, ROLE.ADMIN]
          },
          {
            label: t('menu.add_stock'),
            icon: 'pi pi-truck',
            routerLink: ['/products/add-stock'],
            roles: [ROLE.DOCTOR, ROLE.ADMIN]
          },
          {
            label: t('menu.adjust_stock'),
            icon: 'pi pi-warehouse',
            routerLink: ['/products/adjust-stock'],
            roles: [ROLE.DOCTOR, ROLE.ADMIN]
          }
        ]
      },
      {
        label: t('menu.service'),
        items: [
          {
            label: t('menu.service_list'),
            icon: 'pi pi-wave-pulse',
            routerLink: ['/service/service-list'],
            roles: [ROLE.DOCTOR, ROLE.ADMIN]
          }
        ]
      }
      // {
      //   label: t('menu.documents'),
      //   items: [{ label: t('menu.documents'), icon: 'pi pi-book', routerLink: ['/documents'] }]
      // }
    ];

    this.filteredMenu = model
      .map(group => ({
        ...group,
        items: group.items?.filter((item: any) => !item.roles || item.roles.includes(role))
      }))
      .filter(group => group.items && group.items.length > 0);
  }
}
